import React from 'react';

import { ApiService, ErrorEnum, TaskResultMessageTypeEnum, Urls } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { SPINNER_ICON, TertiaryButton } from 'js/src/components/common/ui/Button';
import {
    DamAuthPopupBroadcastChannelName,
    DamBroadcastMessageType,
    isDamBroadcaseMessage,
} from 'js/src/data/account';
import WebSocketClient from 'js/src/libs/websocket-client';

const HOOTSUITE_PROVIDER = 'hootsuite';

interface SendToHootsuiteButtonProps {
    videoId: number;
}

const isAuthRequiredError = (error: unknown): boolean => {
    const e =
        (error as {
            error?: string;
            body?: { error?: string };
            result?: { error?: string };
        } | null) ?? null;
    return (
        e?.error === ErrorEnum.HOOTSUITE_AUTH_REQUIRED ||
        e?.body?.error === ErrorEnum.HOOTSUITE_AUTH_REQUIRED ||
        e?.result?.error === ErrorEnum.HOOTSUITE_AUTH_REQUIRED
    );
};

/**
 * Share-page CTA that uploads the rendered video to the team's Hootsuite media library.
 * Available to all teams. The same button doubles as the connect entry point: when Hootsuite
 * isn't connected it launches the OAuth consent popup (there is no separate connect surface).
 */
const SendToHootsuiteButton: React.FC<SendToHootsuiteButtonProps> = ({ videoId }) => {
    const [connected, setConnected] = React.useState<boolean>(
        () => window.SERVER?.USER?.hootsuite_connected ?? false,
    );
    const [isSending, setIsSending] = React.useState<boolean>(false);
    const connectChannelRef = React.useRef<BroadcastChannel | null>(null);

    React.useEffect(() => () => connectChannelRef.current?.close(), []);

    const openConnectPopup = React.useCallback(() => {
        const url = Urls.SOCIAL_AUTH.replace('backend', HOOTSUITE_PROVIDER);

        // Close any channel from a previous (possibly abandoned) popup so we don't leak one per click.
        connectChannelRef.current?.close();
        const channel = new BroadcastChannel(DamAuthPopupBroadcastChannelName);
        connectChannelRef.current = channel;
        channel.addEventListener('message', (event) => {
            if (
                !isDamBroadcaseMessage(event.data) ||
                event.data.type !== DamBroadcastMessageType.REFRESH
            ) {
                return;
            }
            channel.close();
            connectChannelRef.current = null;
            // The completion page broadcasts REFRESH on both success and failure (denied consent,
            // auth errors), so only mark connected when the connect actually succeeded.
            if (!event.data.data?.error) {
                setConnected(true);
            }
        });

        window.open(url, 'target', 'width=600,height=700,left=400,top=100');
    }, []);

    const send = React.useCallback(async () => {
        setIsSending(true);
        analytics.track('Send to Hootsuite clicked', { video_id: videoId });
        try {
            const { data } = await ApiService.videoDestinationsHootsuiteCreate({
                path: { video_id: String(videoId) },
            });

            if (!data?.task_id) {
                throw new Error('Hootsuite send unavailable');
            }

            const ws = WebSocketClient.getInstance();
            await ws.waitForMessage(
                data.task_id,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'send_to_hootsuite',
            );

            setIsSending(false);
            Toast.success('Video sent to Hootsuite!');
            analytics.track('Send to Hootsuite succeeded', { video_id: videoId });
        } catch (error) {
            setIsSending(false);
            if (isAuthRequiredError(error)) {
                setConnected(false);
                Toast.error('Please reconnect Hootsuite and try again.');
                openConnectPopup();
                return;
            }
            Toast.error('Failed to send to Hootsuite.');
            analytics.track('Send to Hootsuite failed', { video_id: videoId });
        }
    }, [videoId, openConnectPopup]);

    if (!connected) {
        return (
            <TertiaryButton
                text="Connect Hootsuite"
                iconLeft="link"
                className="w-100 mb-l5-12"
                onClick={openConnectPopup}
            />
        );
    }

    return (
        <TertiaryButton
            text={isSending ? 'Sending...' : 'Send to Hootsuite'}
            iconLeft={isSending ? SPINNER_ICON : 'share'}
            className="w-100 mb-l5-12"
            onClick={send}
            disabled={isSending}
        />
    );
};

export default SendToHootsuiteButton;
